import React, { useEffect, useMemo } from 'react'
import omit from 'lodash/omit'

import Button from '@react/react-spectrum/Button'
import Heading from '@react/react-spectrum/Heading'
import ListDataSource from '@react/react-spectrum/ListDataSource'
import { MenuItem } from '@react/react-spectrum/Menu'
import SplitButton from '@react/react-spectrum/SplitButton'
import { TableView } from '@react/react-spectrum/TableView'

import Delete from '@react/react-spectrum/Icon/Delete'
import Edit from '@react/react-spectrum/Icon/Edit'
import Gears from '@react/react-spectrum/Icon/Gears'
import OnAir from '@react/react-spectrum/Icon/OnAir'

import { useTrigger } from './triggers/hook'
import { useProject } from 'hooks'
import { TRIGGERS_TYPES } from 'config/constants'

import './TriggersForm.scss'

const getIcon = (iconName) => {
  switch (iconName) {
    case 'gears':
    default:
      return <Gears />
  }
}

let columns = [
  {
    title: 'Type',
    key: 'type',
    width: 100,
    sortable: true,
  },
  {
    title: 'Name',
    key: 'name',
    width: 150,
    sortable: true,
  },
  {
    title: 'Setup',
    key: 'setup',
    minWidth: 300,
    sortable: true,
  },
  {
    title: 'Effect',
    key: 'effect',
    width: 300,
    sortable: true,
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 200,
    align: 'center',
  },
]

class TriggersDataSource extends ListDataSource {
  triggers = []

  async setTriggers(triggers) {
    this.triggers = triggers
    return await this.load()
  }
  async load(sortDescriptor) {
    if (sortDescriptor) {
      const field = sortDescriptor.column.key
      return this.triggers.sort((a, b) => {
        return sortDescriptor.direction * a[field].localeCompare(b[field])
      })
    }
    return this.triggers
  }
}

const triggerDataSource = new TriggersDataSource()

const TriggerOptionsEmptyState = () => <Heading size={2}>Triggers are only available in mobile projects.</Heading>

const TriggersForm = () => {
  const { project, config, updateProjectPart, canEdit } = useProject()
  const { TriggerDialog, mapTriggerForDisplay, openDialog } = useTrigger()

  const triggers = project.triggers
  const triggerOptions = config.triggerTypes.map((type) => TRIGGERS_TYPES[type])
  const hasTriggerOptions = triggerOptions && triggerOptions.length

  const addTrigger = (type) => {
    openDialog(type)
  }
  const saveTrigger = (trigger) => {
    if (canEdit) {
      updateProjectPart('triggers', { ...triggers, [trigger.id]: trigger })
    }
  }

  const editTrigger = (triggerId) => openDialog(triggers?.[triggerId])
  const deleteTrigger = (triggerId) => updateProjectPart('triggers', omit(triggers, triggerId))

  // TODO: check if we can safely add the missing dependency
  // eslint-disable-next-line
  const triggersForDisplay = useMemo(() => Object.values(triggers).map(mapTriggerForDisplay), [triggers])

  useEffect(() => {
    if (triggersForDisplay) {
      triggerDataSource.setTriggers(triggersForDisplay).then(() => triggerDataSource.reloadData())
    }
  }, [triggersForDisplay])

  const EmptyState = () => (
    <div className='empty-state-wrapper'>
      <OnAir size={'XL'} />
      <p>No data available</p>
    </div>
  )

  const renderCell = (column, data) => {
    if (column.key === 'actions') {
      return (
        <span>
          <Button variant='tool' aria-label='Edit' icon={<Edit />} onClick={() => editTrigger(data.id)} />
          <Button
            variant='tool'
            aria-label='Delete'
            icon={<Delete />}
            onClick={() => deleteTrigger(data.id)}
            disabled={!canEdit}
          />
        </span>
      )
    }
    return <span>{data[column.key]}</span>
  }

  if (!hasTriggerOptions) {
    return <TriggerOptionsEmptyState />
  }

  return (
    <div className='TriggersForm'>
      <TriggerDialog onSave={saveTrigger} canEdit={canEdit} />

      <SplitButton
        label='Add'
        variant='secondary'
        onClick={(e) => {
          addTrigger(triggerOptions[0].value)
          e.stopPropagation()
        }}
        onSelect={addTrigger}
        disabled={!canEdit}
      >
        {triggerOptions.map((opt) => (
          <MenuItem icon={getIcon(opt.icon)} key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </SplitButton>
      <TableView
        className='triggers-table-view'
        columns={columns}
        dataSource={triggerDataSource}
        renderCell={renderCell}
        renderEmptyView={EmptyState}
        allowsSelection={false}
      ></TableView>
    </div>
  )
}

export default TriggersForm
